import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Box, Typography } from "@mui/material";
import { useUser } from "../../context/UserContext";
import PlanBox from "../../components/PlanBox";
import NoResults from "../../components/NoResults";
import { useLanguage } from "../../context/LanguageContext";
import { Colors } from "../../styles/theme";

const Pricing = () => {
  const [planType, setPlanType] = useState("");
  const [subscriptionStatus, setSubscriptionStatus] = useState("");
  const [introDiscountUsed, setIntroDiscountUsed] = useState(false);
  const { user } = useUser();

  const { translate, selectedLanguage } = useLanguage();
  const plans = translate("plans");
  const navigate = useNavigate();
  const location = useLocation();
  const currentPath = `${location.pathname}${location.search}${location.hash}`;

  useEffect(() => {
    let cancelled = false;
    setPlanType("");
    setSubscriptionStatus("");
    setIntroDiscountUsed(false);

    if (user) {
      const fetchSubscription = async () => {
        try {
          const [{ doc, getDoc }, { db }] = await Promise.all([
            import("firebase/firestore"),
            import("../../config/firebase"),
          ]);
          const snapshot = await getDoc(doc(db, "Clients", user.uid));
          if (cancelled) return;
          const subscription = snapshot.data()?.subscription;
          if (subscription) {
            setPlanType(subscription.planType || "");
            setSubscriptionStatus(subscription.status || "");
            const hasSuccessfulSubscription =
              subscription.status === "active" ||
              subscription.paymentStatus === "paid" ||
              subscription.invoiceStatus === "paid";
            setIntroDiscountUsed(Boolean(
              subscription.introDiscountApplied ||
                subscription.introDiscountRedeemed ||
                hasSuccessfulSubscription
            ));
          }
        } catch (error) {
          if (!cancelled) console.error("Error getting user document:", error);
        }
      };
      fetchSubscription();
    }

    return () => { cancelled = true; };
  }, [user]);

  const checkout = async (selectedPlan) => {
    if (!selectedPlan) return;

    if (!user) {
      navigate("/signup/", { state: { from: currentPath } });
      return;
    }

    // Check if subscription is active instead of just planType
    if (subscriptionStatus === "active") {
      navigate("/profile/subscription");
      return;
    }

    try {
      const [{ doc, getDoc }, { db }, { createSubscriptionCheckoutSession }] = await Promise.all([
        import("firebase/firestore"),
        import("../../config/firebase"),
        import("../../services/createSubscriptionCheckoutSession"),
      ]);
      const userRef = doc(db, "Clients", user.uid);
      const docSnap = await getDoc(userRef);
      const userData = docSnap.data();
      const companyInfo = userData?.companyInfo;

      if (!companyInfo) {
        navigate("/company-info", { state: { plan: selectedPlan.id } });
        return;
      }

      await createSubscriptionCheckoutSession({
        plan: selectedPlan.id,
        users: 0,
      });
    } catch (error) {
      console.error("Error starting checkout:", error);
    }
  };

  const getButtonText = (plan) => {
    const normalizedPlanId = plan.id?.toLowerCase();

    if (planType === normalizedPlanId && subscriptionStatus === "active") return "subscribed";
    if (planType === normalizedPlanId && introDiscountUsed) return "resubscribe";
    if (introDiscountUsed) return "subscribe";
    return "get started";
  };

  const planCount = Array.isArray(plans) ? plans.length : 0;
  const showIntroOffer = !introDiscountUsed;

  return (
    <Box
      component="section"
      id="pricing"
      sx={{
        width: "100%",
        maxWidth: "100%",
        boxSizing: "border-box",
        overflowX: "hidden",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "#F7F9FC",
        scrollMarginTop: "80px",
        direction: selectedLanguage === "ar" ? "rtl" : "ltr",
        padding: {
          xs: "48px 20px 56px",
          sm: "64px 20px 72px",
          md: "80px 20px 96px",
        },
      }}
    >
      <Box
        sx={{
          width: "100%",
          maxWidth: "980px",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <Typography
          variant="subtitle2"
          component="p"
          color={Colors.primary}
          mb={{ xs: 1.25, sm: 2 }}
          sx={{
            fontWeight: 800,
            letterSpacing: 3,
            textAlign: "center",
            textTransform: "uppercase",
          }}
        >
          {translate("transparent pricing")}
        </Typography>
        <Typography
          component="h2"
          sx={{
            color: Colors.navy,
            fontSize: { xs: "2.35rem", md: "4rem" },
            fontWeight: 800,
            letterSpacing: 0,
            lineHeight: 1.08,
            textAlign: "center",
          }}
        >
          {translate("pricing hero title")}
        </Typography>
        <Typography
          variant="h6"
          component="p"
          color={Colors.text}
          mt={{ xs: 1.5, sm: 2 }}
          sx={{
            fontWeight: 500,
            lineHeight: 1.65,
            textAlign: "center",
          }}
        >
          {translate(showIntroOffer ? "pricing trial note" : "plans header2 returning")}
        </Typography>
      </Box>
      <Box
        sx={{
          display: "flex",
          width: "100%",
          maxWidth: "none",
          minWidth: 0,
          boxSizing: "border-box",
          alignItems: "stretch",
          flexWrap: "wrap",
          gap: { xs: 1.5, md: 2 },
          mt: { xs: 3, sm: 4, md: 4.5 },
          px: { xs: 1, sm: 3, md: 5 },
          "@media (max-width:600px) and (max-height:750px)": {
            px: 0,
          },
        }}
      >
        {Array.isArray(plans) && plans.length > 0 ? (
          plans.map((plan) => (
            <PlanBox
              key={plan.id}
              planName={plan.planName}
              description={plan.description}
              price={plan.price}
              regularPrice={plan.regularPrice}
              showIntroOffer={showIntroOffer}
              planFeatures={plan.planFeatures.map(translate)}
              handleOnClick={() => checkout(plan)}
              buttonText={getButtonText(plan)}
              planCount={planCount || 1}
            />
          ))
        ) : (
          <NoResults />
        )}
      </Box>
    </Box>
  );
};

export default Pricing;
